import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

// Hoisted to module scope — defining this inside PaymentSettings gave it a
// new identity on every keystroke (every setSettings re-render), so React
// unmounted/remounted the <input> each time and the cursor lost focus.
function Field({ label, field, type = 'text', placeholder = '', help = '', value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" type={type} value={value || ''} onChange={e => onChange(field, e.target.value)} placeholder={placeholder} />
      {help && <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4 }}>{help}</div>}
    </div>
  );
}

export default function PaymentSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [removeQrCode, setRemoveQrCode] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [cowImageFile, setCowImageFile] = useState(null);
  const [removeCowImage, setRemoveCowImage] = useState(false);
  const [bgAudioFile, setBgAudioFile] = useState(null);
  const [removeBgAudio, setRemoveBgAudio] = useState(false);

  useEffect(() => {
    API.get('/settings/admin').then(r => { setSettings(r.data || {}); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(settings).forEach(([k, v]) => {
        if (k === 'qrCodeImage' || k === 'logoImage' || k === 'defaultCowImage' || k === 'backgroundAudio') return; // set by the server from the uploaded file, not sent back
        fd.append(k, v ?? '');
      });
      if (qrCodeFile) fd.append('qrCodeImage', qrCodeFile);
      else if (removeQrCode) fd.append('removeQrCodeImage', 'true');
      if (logoFile) fd.append('logoImage', logoFile);
      else if (removeLogo) fd.append('removeLogoImage', 'true');
      if (cowImageFile) fd.append('defaultCowImage', cowImageFile);
      else if (removeCowImage) fd.append('removeDefaultCowImage', 'true');
      if (bgAudioFile) fd.append('backgroundAudio', bgAudioFile);
      else if (removeBgAudio) fd.append('removeBackgroundAudio', 'true');
      await API.put('/settings', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setQrCodeFile(null);
      setRemoveQrCode(false);
      setLogoFile(null);
      setRemoveLogo(false);
      setCowImageFile(null);
      setRemoveCowImage(false);
      setBgAudioFile(null);
      setRemoveBgAudio(false);
      const r = await API.get('/settings/admin');
      setSettings(r.data || {});
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    setSaving(false);
  };

  const updateField = (field, value) => setSettings(p => ({ ...p, [field]: value }));
  const qrCodeImageUrl = settings.qrCodeImage ? (settings.qrCodeImage.startsWith('http') ? settings.qrCodeImage : `${API_BASE}${settings.qrCodeImage}`) : null;
  const logoImageUrl = settings.logoImage ? (settings.logoImage.startsWith('http') ? settings.logoImage : `${API_BASE}${settings.logoImage}`) : null;
  const cowImageUrl = settings.defaultCowImage ? (settings.defaultCowImage.startsWith('http') ? settings.defaultCowImage : `${API_BASE}${settings.defaultCowImage}`) : null;
  const bgAudioUrl = settings.backgroundAudio ? (settings.backgroundAudio.startsWith('http') ? settings.backgroundAudio : `${API_BASE}${settings.backgroundAudio}`) : null;

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <div>
      <h2 className="admin-page-title">⚙️ Settings & Configuration</h2>

      {/* Site Identity */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>🏷️ Site Identity</h3>
        <Field label="Site / Temple Name" field="siteName" value={settings.siteName} onChange={updateField} placeholder="Sri Bramaramba Mallikarjuna Swami Goshala" help="Shown in the header, footer, and browser tab title across the website" />

        <div className="form-group">
          <label className="form-label">Application Logo</label>
          <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 10 }}>
            Shown in the site header and footer in place of the default cow icon. Uploading a new logo replaces the current one — only one logo is active at a time.
          </div>
          {logoImageUrl && !removeLogo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <img src={logoImageUrl} alt="Current application logo" style={{ width: 64, height: 64, objectFit: 'contain', border: '1.5px solid var(--border)', borderRadius: '50%', background: '#fff' }} />
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setRemoveLogo(true)}>Remove Logo</button>
            </div>
          )}
          {removeLogo && (
            <div style={{ fontSize: '0.85rem', color: '#c62828', marginBottom: 10 }}>
              Logo will be removed on save — the default cow icon will be used instead.
              <button type="button" onClick={() => setRemoveLogo(false)} style={{ marginLeft: 10, background: 'none', border: '1.5px solid var(--border)', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Undo</button>
            </div>
          )}
          {logoFile && (
            <div style={{ fontSize: '0.85rem', color: 'var(--green)', marginBottom: 10 }}>
              New logo selected — will be uploaded on save.
              <button type="button" onClick={() => setLogoFile(null)} style={{ marginLeft: 10, background: 'none', border: '1.5px solid var(--border)', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Undo</button>
            </div>
          )}
          <input type="file" accept="image/*" onChange={e => { setLogoFile(e.target.files[0]); setRemoveLogo(false); }} />
        </div>

        <div className="form-group" style={{ marginTop: 20 }}>
          <label className="form-label">Default Cow Photo</label>
          <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 10 }}>
            Shown on any cow's card (Home page, Adopt a Cow) whenever that specific cow doesn't have its own photo uploaded yet — replaces the plain 🐂 icon placeholder.
          </div>
          {cowImageUrl && !removeCowImage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <img src={cowImageUrl} alt="Current default cow photo" style={{ width: 80, height: 80, objectFit: 'cover', border: '1.5px solid var(--border)', borderRadius: 8, background: '#fff' }} />
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setRemoveCowImage(true)}>Remove Photo</button>
            </div>
          )}
          {removeCowImage && (
            <div style={{ fontSize: '0.85rem', color: '#c62828', marginBottom: 10 }}>
              Photo will be removed on save — the plain 🐂 icon will be used instead.
              <button type="button" onClick={() => setRemoveCowImage(false)} style={{ marginLeft: 10, background: 'none', border: '1.5px solid var(--border)', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Undo</button>
            </div>
          )}
          {cowImageFile && (
            <div style={{ fontSize: '0.85rem', color: 'var(--green)', marginBottom: 10 }}>
              New photo selected — will be uploaded on save.
              <button type="button" onClick={() => setCowImageFile(null)} style={{ marginLeft: 10, background: 'none', border: '1.5px solid var(--border)', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Undo</button>
            </div>
          )}
          <input type="file" accept="image/*" onChange={e => { setCowImageFile(e.target.files[0]); setRemoveCowImage(false); }} />
        </div>
      </div>

      {/* Background Audio */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 4 }}>🕉️ Home Page Background Audio</h3>
        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 16 }}>
          Plays softly on loop in the background when a visitor opens the home page (e.g. Om chanting). Uploading a new track replaces the current one — only one is active at a time. Keep the file short (it loops) and under 10 MB.
        </p>
        {bgAudioUrl && !removeBgAudio && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
            <audio controls src={bgAudioUrl} style={{ height: 36 }} />
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setRemoveBgAudio(true)}>Remove Audio</button>
          </div>
        )}
        {removeBgAudio && (
          <div style={{ fontSize: '0.85rem', color: '#c62828', marginBottom: 10 }}>
            Background audio will be removed on save — the home page will be silent.
            <button type="button" onClick={() => setRemoveBgAudio(false)} style={{ marginLeft: 10, background: 'none', border: '1.5px solid var(--border)', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Undo</button>
          </div>
        )}
        {bgAudioFile && (
          <div style={{ fontSize: '0.85rem', color: 'var(--green)', marginBottom: 10 }}>
            New audio selected — will be uploaded on save.
            <button type="button" onClick={() => setBgAudioFile(null)} style={{ marginLeft: 10, background: 'none', border: '1.5px solid var(--border)', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Undo</button>
          </div>
        )}
        <input type="file" accept="audio/*" onChange={e => { setBgAudioFile(e.target.files[0]); setRemoveBgAudio(false); }} />
        <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 8 }}>
          Browsers block sound from autoplaying until the visitor interacts with the page, so it starts on their first click/tap; a small speaker button lets them mute it any time.
        </div>
      </div>

      {/* Razorpay Payment Gateway */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 4 }}>💳 Razorpay Payment Gateway</h3>
        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 20 }}>
          Get your keys from the <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer">Razorpay Dashboard</a> → Settings → API Keys.
          Keys starting with <strong>rzp_test_</strong> are for testing; switch to live mode keys when you're ready to accept real payments.
        </p>
        <div className="form-row">
          <Field label="Key ID" field="razorpayKeyId" placeholder="rzp_test_xxxxxxxxxxxx" help="Safe to expose to the browser" value={settings.razorpayKeyId} onChange={updateField} />
          <Field label="Key Secret" field="razorpayKeySecret" type="password" placeholder="Your Razorpay key secret" help="Keep this secret! Never shared with the browser." value={settings.razorpayKeySecret} onChange={updateField} />
        </div>
      </div>

      {/* UPI / QR Code */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>📱 UPI / QR Code Donation</h3>
        <div className="form-row">
          <Field label="UPI ID" field="upiId" placeholder="goshala@okaxis" help="Your registered UPI ID for QR code" value={settings.upiId} onChange={updateField} />
          <Field label="QR Code Description" field="donationQrText" placeholder="Scan to donate to Goshala" value={settings.donationQrText} onChange={updateField} />
        </div>
        <div className="form-group">
          <label className="form-label">QR Code Image (optional)</label>
          <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 10 }}>
            Upload your bank/UPI app's official QR code image to show it on the website instead of an auto-generated one. Not required — if left empty, a QR code is generated from the UPI ID above.
          </div>
          {qrCodeImageUrl && !removeQrCode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <img src={qrCodeImageUrl} alt="Current QR code" style={{ width: 120, height: 120, objectFit: 'contain', border: '1.5px solid var(--border)', borderRadius: 8, background: '#fff' }} />
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setRemoveQrCode(true)}>Remove Image</button>
            </div>
          )}
          {removeQrCode && (
            <div style={{ fontSize: '0.85rem', color: '#c62828', marginBottom: 10 }}>
              QR code image will be removed on save — a generated QR code will be used instead.
              <button type="button" onClick={() => setRemoveQrCode(false)} style={{ marginLeft: 10, background: 'none', border: '1.5px solid var(--border)', borderRadius: 6, padding: '2px 10px', cursor: 'pointer' }}>Undo</button>
            </div>
          )}
          <input type="file" accept="image/*" onChange={e => { setQrCodeFile(e.target.files[0]); setRemoveQrCode(false); }} />
        </div>
      </div>

      {/* Trust Info */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 4 }}>🏛️ Trust Information</h3>
        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 20 }}>
          Also used as the header/footer on printed donation receipts, together with the bank details below.
        </p>
        <div className="form-row">
          <Field label="Trust Name" field="trustName" value={settings.trustName} onChange={updateField} />
          <Field label="Trust Registration Number" field="trustRegNo" value={settings.trustRegNo} onChange={updateField} />
        </div>
        <div className="form-row">
          <Field label="Trust PAN Number" field="trustPanNo" value={settings.trustPanNo} onChange={updateField} placeholder="AAKTS5994D" help="Printed on donation receipts" />
          <Field label="80G Exemption Number" field="section80GNo" value={settings.section80GNo} onChange={updateField} />
        </div>
        <div className="form-row">
          <Field label="Contact Email" field="contactEmail" type="email" value={settings.contactEmail} onChange={updateField} />
          <Field label="Contact Phone" field="contactPhone" value={settings.contactPhone} onChange={updateField} />
        </div>
        <div className="form-row">
          <Field label="Website URL" field="websiteUrl" placeholder="www.example.com" value={settings.websiteUrl} onChange={updateField} />
          <div />
        </div>
        <Field label="Address" field="address" placeholder="Full address..." value={settings.address} onChange={updateField} />
      </div>

      {/* Bank Details */}
      <div className="admin-form-card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>🏦 Bank Account Details</h3>
        <div className="form-row">
          <Field label="Bank Name" field="bankName" value={settings.bankName} onChange={updateField} />
          <Field label="Account Number" field="bankAccount" value={settings.bankAccount} onChange={updateField} />
        </div>
        <div className="form-row">
          <Field label="IFSC Code" field="bankIFSC" value={settings.bankIFSC} onChange={updateField} />
          <Field label="Branch" field="bankBranch" value={settings.bankBranch} onChange={updateField} />
        </div>
      </div>

      <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
        {saving ? '⏳ Saving...' : '💾 Save All Settings'}
      </button>
    </div>
  );
}
